import { useRef } from "react";
import { Controller } from "react-hook-form";
import { Autocomplete, TextField, Chip, createFilterOptions } from "@mui/material";
import { Popper } from "@material-ui/core";

const AutocompleteSelect = ({
  name,
  control,
  label,
  options = [],
  required = false,
  labelprop = "label",
  disabled = false,
  disableClearable = false,
  multiple = false,
  restrictRemove = false,
  hasAddNew = false,
  optionHeight = 200,
  disablePortal = true,
  onOpen = undefined,
}) => {
  const labelWithAsterisk = required
    ? (typeof label === 'string' ? `${label} *` : <>{label}&nbsp;*</>)
    : label;

  // Base filter
  const filter = createFilterOptions({
    stringify: (option) =>
      labelprop ? option?.[labelprop]?.toString?.() || "" : option?.label || "",
  });

  // Custom filter to inject "Add ..." option
  function getFilterOptions(opts, params) {
    const filtered = filter(opts, params);
    const input = params.inputValue?.trim();

    if (
      hasAddNew &&
      input &&
      filtered?.length === 0
    ) {
      filtered.push({
        inputValue: input,
        label: `Add "${input}"`,
      });
    }

    return filtered;
  }
// Base getOptionLabel
  function getOptionLabel(option) {
    if (typeof option === "string") return option;
    if (hasAddNew && option.inputValue) return `Add "${option.inputValue}"`;
    return option?.[labelprop] || option?.label || "";
  }
  // isOptionEqualToValue function
  const isOptionEqualToValue = (option, value) =>
    option?.[labelprop] === value?.[labelprop];

  const initialValuesRef = useRef(null);
  // onChange handler with restrictRemove logic
  function onChangeSelect(newValue, reason, field, initialValues) {
    if (restrictRemove && multiple) {
      if (reason === "removeOption" || reason === "clear") {
        let lockedItemRemoved = false;

        const newMap = new Map(
          (newValue || []).map((v) => [JSON.stringify(v), true])
        );
        const initMap = new Map(
          (initialValues || []).map((v) => [JSON.stringify(v), true])
        );

        for (const item of field.value || []) {
          const key = JSON.stringify(item);
          if (!newMap.has(key) && initMap.has(key)) {
            lockedItemRemoved = true;
            break;
          }
        }

        if (lockedItemRemoved) return;
        if (reason === "clear") return;
      }
      field.onChange(newValue);
    } else if (hasAddNew && newValue?.inputValue) {
      const newOption = {
        label: newValue.inputValue,
        value: newValue.inputValue,
      };
      field.onChange(newOption);
    } else {
      field.onChange(newValue);
    }
  }

  // Render tags with restrictRemove logic
 function onRenderTags(value, getTagProps, initialValues, restrictRemove) {
  return value.map((option, index) => {
    const isPrefilled = initialValues.some((init) => //enahnce nested
      isOptionEqualToValue(init, option)
    );
    const tagProps = getTagProps({ index });

    return (
      <Chip
        {...tagProps}
        label={getOptionLabel(option)}
        onDelete={
          restrictRemove && isPrefilled
            ? undefined
            : tagProps.onDelete
        }
      />
    );
  })
}

function CustomPopper(props) {
  return (
    <Popper
      {...props}
      placement="bottom-start"
      modifiers={[
        {
          name: "flip",
          enabled: true,
          options: {
            altBoundary: true,
            rootBoundary: "viewport",
          },
        },
      ]}
    />
  );
}



  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => {
        if (initialValuesRef.current === null && field.value) {
          initialValuesRef.current = Array.isArray(field.value)
            ? [...field.value]
            : [field.value];
        }

        const initialValues = initialValuesRef.current || [];

        return (
          <Autocomplete
            multiple={multiple}
            PopperComponent={CustomPopper}
            ListboxProps={{
              sx: {
                maxHeight: optionHeight,
                overflowY: "auto"
              }
            }}
            options={options}
            onOpen={onOpen}
            fullWidth
            disableClearable={disableClearable}
            getOptionLabel={(option) => getOptionLabel(option)}
            disabled={disabled}
            isOptionEqualToValue={isOptionEqualToValue}
            value={field.value || (multiple ? [] : null)}
            filterOptions={(opts, params) => getFilterOptions(opts, params)}
             onChange={(_, newValue, reason) =>
              onChangeSelect(newValue, reason, field, initialValues)
            }
            renderTags={(value, getTagProps) => onRenderTags(value, getTagProps, initialValues, restrictRemove)}
            renderInput={(params) => (
              <TextField
                {...params}
                label={labelWithAsterisk}
                error={!!error}
                helperText={error?.message}
                fullWidth
              />
            )}
          />
        );
      }}
    />
  );
};

export default AutocompleteSelect;